import { useState } from 'react';
import { LuxuryTable } from '@/components/Luxury';
import { formatPercent } from '../../utils/formatters';
import type { ExitSensitivityMatrix, ExitSensitivityMatrixCell } from '../../types/calculations';

interface ExitCapSensitivityMatrixProps {
  matrix: ExitSensitivityMatrix;
  purchasePrice?: number;
  className?: string;
}

type TabKey = 'hold' | 'price';

/**
 * Row label for each exit cap offset.
 */
function rowLabel(offsetBps: number): { label: string; colorClass: string } {
  if (offsetBps < 0) {
    return { label: `Optimistic (${offsetBps}bp)`, colorClass: 'text-emerald-400' };
  }
  if (offsetBps > 0) {
    return { label: `Pessimistic (+${offsetBps}bp)`, colorClass: 'text-rose-400' };
  }
  return { label: 'Base Case', colorClass: 'text-ivory' };
}

/**
 * Format IRR + equity multiple for a cell.
 */
function CellValue({
  cell,
  isBase,
  irrColorClass,
}: {
  cell: ExitSensitivityMatrixCell;
  isBase: boolean;
  irrColorClass: string;
}) {
  const irrStr = cell.irr != null ? formatPercent(cell.irr) : '—';
  const emStr = `${cell.equity_multiple.toFixed(2)}x`;
  return (
    <div className="flex flex-col items-end gap-0.5">
      <span className={`font-mono text-sm font-semibold tabular-nums ${irrColorClass}`}>
        {irrStr}
      </span>
      <span className={`font-mono text-xs tabular-nums ${isBase ? 'text-gold/80' : 'text-stone-500'}`}>
        {emStr}
      </span>
    </div>
  );
}

/**
 * Tab 2 — Exit Cap x Purchase Price (Est.).
 *
 * Derived client-side from the base hold period (5yr column).
 * Price variations: -10%, Base, +10% of the original purchase price.
 *
 * EM formula: (net_proceeds + cumulative_cf) / (base_equity * priceFactor)
 * Net proceeds and cumulative CF stay fixed (sale price is exit-cap driven, not purchase-price driven).
 * Only equity required changes with purchase price.
 * IRR is not re-computed (no backend call available).
 */
function PurchasePriceTab({
  matrix,
  purchasePrice,
}: {
  matrix: ExitSensitivityMatrix;
  purchasePrice?: number;
}) {
  const priceVariations = [-0.1, 0, 0.1];
  const priceLabels = ['-10% Price', 'Base Price', '+10% Price'];

  // Find the 5yr column index (or fallback to index 1 = 5yr in [3,5,7,10])
  const baseColIdx = matrix.col_hold_periods.indexOf(5) >= 0
    ? matrix.col_hold_periods.indexOf(5)
    : Math.min(1, matrix.col_hold_periods.length - 1);

  return (
    <div>
      <LuxuryTable>
        <thead>
          <tr>
            <th className="text-left">Scenario</th>
            {priceLabels.map((label) => (
              <th key={label} className="text-right">{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.row_offsets.map((offsetBps, rowIdx) => {
            const { label, colorClass } = rowLabel(offsetBps);
            const baseCell = matrix.cells[rowIdx]?.[baseColIdx];
            const isBaseRow = offsetBps === 0;

            return (
              <tr
                key={offsetBps}
                className={isBaseRow ? 'bg-gold/5' : ''}
              >
                <td className={`text-sm font-medium ${colorClass}`}>{label}</td>
                {priceVariations.map((variation, varIdx) => {
                  if (!baseCell) {
                    return (
                      <td key={varIdx} className="num text-stone-500">—</td>
                    );
                  }
                  const priceFactor = 1 + variation;

                  // Correct EM: net proceeds + cumulative CF stay fixed (sale driven by exit cap),
                  // only equity changes with purchase price.
                  // Back-compute base equity from stored EM, net_proceeds, and cumulative_cf.
                  let adjustedEM: number;
                  if (
                    baseCell.net_proceeds != null &&
                    baseCell.cumulative_cf != null &&
                    baseCell.equity_multiple > 0
                  ) {
                    const totalReturn = baseCell.net_proceeds + baseCell.cumulative_cf;
                    // base_equity = totalReturn / equity_multiple
                    const baseEquity = totalReturn / baseCell.equity_multiple;
                    // At adjusted price: equity scales by priceFactor, proceeds stay fixed
                    adjustedEM = totalReturn / (baseEquity * priceFactor);
                  } else {
                    // Fallback to proportional approximation if cell data is missing
                    adjustedEM = baseCell.equity_multiple / priceFactor;
                  }

                  const isBaseVariation = variation === 0;
                  const isBaseCell = isBaseRow && isBaseVariation;

                  return (
                    <td
                      key={varIdx}
                      className={`num ${isBaseCell ? 'ring-1 ring-gold/60 bg-gold/10 rounded' : ''}`}
                    >
                      <div className="flex flex-col items-end gap-0.5">
                        <span className="font-mono text-xs text-stone-500 italic">est.</span>
                        <span className={`font-mono text-sm font-semibold tabular-nums ${colorClass}`}>
                          {adjustedEM.toFixed(2)}x
                        </span>
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </LuxuryTable>
      <p className="mt-3 text-xs text-stone-500">
        Based on 5-year hold (base column). EM approximation: holds exit proceeds constant,
        adjusts only equity required. Actual IRR varies — use the Assumptions drawer for
        precise recalculation. Purchase price variations: {purchasePrice
          ? `${(purchasePrice * 0.9 / 1000).toFixed(0)}K / ${(purchasePrice / 1000).toFixed(0)}K / ${(purchasePrice * 1.1 / 1000).toFixed(0)}K`
          : '−10% / Base / +10%'}.
      </p>
    </div>
  );
}

/**
 * ExitCapSensitivityMatrix — 2D sensitivity matrix with two tabs.
 *
 * Tab 1: Exit Cap x Hold Period (3/5/7/10yr)
 * Tab 2: Exit Cap x Purchase Price (−10%/Base/+10%)
 *
 * Rows = exit cap offsets from config EXIT_CAP_SENSITIVITY_BPS
 * Each cell shows IRR + equity multiple (e.g. "14.2% / 2.1x")
 * Base case cell (offset=0, hold=5yr) highlighted with gold ring
 */
export function ExitCapSensitivityMatrix({
  matrix,
  purchasePrice,
  className = '',
}: ExitCapSensitivityMatrixProps) {
  const [activeTab, setActiveTab] = useState<TabKey>('hold');

  if (!matrix || !matrix.cells || matrix.cells.length === 0) {
    return null;
  }

  const basePct = (matrix.base_exit_cap * 100).toFixed(2);
  const entryPct = (matrix.entry_cap * 100).toFixed(2);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Tab buttons */}
      <div className="flex gap-1 border-b border-slate-dark">
        {(['hold', 'price'] as TabKey[]).map((tab) => {
          const label = tab === 'hold' ? 'Exit Cap × Hold Period' : 'Exit Cap × Purchase Price (Est.)';
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                isActive
                  ? 'border-gold text-gold'
                  : 'border-transparent text-stone-500 hover:text-stone-400'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Tab 1: Exit Cap x Hold Period */}
      {activeTab === 'hold' && (
        <LuxuryTable>
          <thead>
            <tr>
              <th className="text-left">Scenario</th>
              {matrix.col_hold_periods.map((yr) => (
                <th key={yr} className="text-right">{yr}yr Hold</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.row_offsets.map((offsetBps, rowIdx) => {
              const { label, colorClass } = rowLabel(offsetBps);
              const isBaseRow = offsetBps === 0;
              const baseColIdx = matrix.col_hold_periods.indexOf(5);

              return (
                <tr
                  key={offsetBps}
                  className={isBaseRow ? 'bg-gold/5' : ''}
                >
                  <td className={`text-sm font-medium ${colorClass}`}>{label}</td>
                  {matrix.col_hold_periods.map((yr, colIdx) => {
                    const cell = matrix.cells[rowIdx]?.[colIdx];
                    if (!cell) return <td key={yr} className="num text-stone-500">—</td>;

                    const isBaseCell = isBaseRow && colIdx === baseColIdx;
                    return (
                      <td
                        key={yr}
                        className={`num ${isBaseCell ? 'ring-1 ring-gold/60 bg-gold/10 rounded' : ''}`}
                      >
                        <CellValue
                          cell={cell}
                          isBase={isBaseCell}
                          irrColorClass={colorClass}
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </LuxuryTable>
      )}

      {/* Tab 2: Exit Cap x Purchase Price */}
      {activeTab === 'price' && (
        <PurchasePriceTab matrix={matrix} purchasePrice={purchasePrice} />
      )}

      {/* Footer */}
      <p className="text-xs text-stone-500">
        Base exit cap: {basePct}% (entry cap {entryPct}% &minus; 100bp).
        Rows show &plusmn;50bp exit cap scenarios. Each cell: IRR / Equity Multiple.
      </p>
    </div>
  );
}
